import React, { createContext, Dispatch, FunctionComponent, PropsWithChildren, SetStateAction, useContext } from 'react'
import { SupportedLocale } from '@amzn/aapn-stage-dive-shared-types'
import { useTranslation } from 'react-i18next'

interface InternalLanguageContextInterface {
  handleLanguageChange: (language: SupportedLocale) => void
}

interface LanguageContextInterface {
  language: SupportedLocale
}

interface LanguageContextProviderProps {
  defaultValue: SupportedLocale
}

interface InternalLanguageContextProviderProps {
  defaultValue: Dispatch<SetStateAction<SupportedLocale>>
}

export const LanguageContext = createContext<LanguageContextInterface>({ language: 'en-US' })
export const InternalLanguageContext = createContext<InternalLanguageContextInterface>({ handleLanguageChange: (language: SupportedLocale) => {} })

export const languageOverrides = (languagePicked: SupportedLocale): SupportedLocale => {
  switch (languagePicked) {
    case 'en-AE':
      return 'en-US'
    default:
      return languagePicked
  }
}

export const InternalLanguageContextProvider: FunctionComponent<InternalLanguageContextProviderProps> = (
  props: PropsWithChildren<InternalLanguageContextProviderProps>,
) => {
  const setLanguageContext = props.defaultValue
  const { i18n } = useTranslation()

  const handleLanguageChange = (language: SupportedLocale): void => {
    setLanguageContext(language)
    i18n.changeLanguage(languageOverrides(language) ?? i18n.language)
  }

  return <InternalLanguageContext.Provider value={{ handleLanguageChange }}>{props.children}</InternalLanguageContext.Provider>
}

export const LanguageContextProvider: FunctionComponent<LanguageContextProviderProps> = (props: PropsWithChildren<LanguageContextProviderProps>) => {
  const language = props.defaultValue

  return <LanguageContext.Provider value={{ language }}>{props.children}</LanguageContext.Provider>
}

export const useLanguageContext: () => LanguageContextInterface = () => useContext(LanguageContext)
export const useLanguageSetterContext: () => InternalLanguageContextInterface = () => useContext(InternalLanguageContext)

export const getLanguageContextFallback: () => SupportedLocale = () => {
  const { language } = useLanguageContext()

  if (language === 'en-US') {
    const sessionStorageLanguageContext = (sessionStorage.getItem('languageContext') ?? 'en-US') as SupportedLocale
    return sessionStorageLanguageContext ? sessionStorageLanguageContext : language
  }
  return language
}
